'use client';

import { useEffect, useRef, useState } from 'react';
import * as MainPageIcons from './MainPageIcons';

const TECHS = [
  { label: 'Flutter', color: '#54C5F8', icon: <MainPageIcons.FlutterIcon />, delay: '0s', animClass: 'animate-wave' },
  { label: 'Node.js', color: '#68A063', icon: <MainPageIcons.NodeJs />, delay: '0.4s', animClass: 'animate-wave2' },
  { label: 'PHP', color: '#8892BF', icon: <MainPageIcons.PhpIcon />, delay: '0.8s', animClass: 'animate-wave3' },
  { label: 'Python', color: '#4B8BBE', icon: <MainPageIcons.Python />, delay: '1.2s', animClass: 'animate-wave4' },
  { label: 'React', color: '#61DAFB', icon: <MainPageIcons.ReacJs />, delay: '1.6s', animClass: 'animate-wave5' },
  { label: 'Next.js', color: '#FFFFFF', icon: <MainPageIcons.NextIcon />, delay: '2.0s', animClass: 'animate-wave6' },
  { label: 'Android', color: '#3DDC84', icon: <MainPageIcons.Andorid />, delay: '0.2s', animClass: 'animate-wave2' },
  { label: 'iOS', color: '#A2AAAD', icon: <MainPageIcons.Ios />, delay: '0.6s', animClass: 'animate-wave3' },
  { label: 'Java', color: '#F89820', icon: <MainPageIcons.JavaIcon />, delay: '1.0s', animClass: 'animate-wave4' },
  { label: 'Tailwind', color: '#38BDF8', icon: <MainPageIcons.Tailwind />, delay: '1.4s', animClass: 'animate-wave5' },
  { label: 'Bootstrap', color: '#7952B3', icon: <MainPageIcons.Bootstrap />, delay: '1.8s', animClass: 'animate-wave6' },
];

function TechCard({ tech, visible }: { tech: typeof TECHS[0]; visible: boolean }) {
  return (
    <div
      className={`${tech.animClass} flex flex-col items-center gap-2 group transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ transitionDelay: tech.delay }}
    >
      <div className="w-[64px] h-[64px] md:w-[80px] md:h-[80px] flex items-center justify-center rounded-[16px] border border-[#474588]/60 bg-gradient-to-b from-[#1F1E3C] to-[#23215F] p-[14px] group-hover:border-[#1586FF]/50 group-hover:shadow-[0_0_20px_rgba(21,134,255,0.15)] transition-all duration-300">
        {tech.icon}
      </div>
      <span
        className="text-[11px] md:text-[12px] schibsted-grotesk font-medium opacity-60 group-hover:opacity-100 transition-opacity"
        style={{ color: tech.color }}
      >
        {tech.label}
      </span>
    </div>
  );
}

export default function TechStack() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.25 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="pt-[32px] pb-[60px] md:pt-[48px] md:pb-[120px] bg-[#01001E] relative overflow-hidden border-t border-white/[0.05]">

      {/* Background glow */}
      <div className="absolute left-1/2 -translate-x-1/2 -bottom-32 sm:-bottom-40 md:-bottom-48 lg:-bottom-56 w-full max-w-[900px] h-[280px] sm:h-[320px] md:h-[350px] lg:h-[382px] bg-[#2478FF] opacity-40 rounded-[1289px] blur-[100px] pointer-events-none" />

      {/* Dot grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="px-4 md:container relative z-10">

        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <span className="text-white/60 text-sm font-medium schibsted-grotesk">Technology</span>
          </span>
          <h2 className="font-NeueMontreal text-[32px] md:text-[48px] font-bold leading-[120%] mb-4"
            style={{
              background: 'linear-gradient(90deg, #35A8FF 0%, #D3A6FF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
            Built on Proven Technologies
          </h2>
          <p className="schibsted-grotesk text-white/40 text-[16px] md:text-[18px] max-w-xl mx-auto leading-relaxed">
            Every product we ship is built on battle-tested, scalable, and modern technology — no shortcuts.
          </p>
        </div>

        {/* Tech grid */}
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-6 lg:grid-cols-11 gap-x-4 gap-y-8 justify-items-center items-center">
          {TECHS.map((tech) => (
            <TechCard key={tech.label} tech={tech} visible={visible} />
          ))}
        </div>

        {/* Connection lines — decorative SVG */}
        <div className="relative mt-16 overflow-hidden">
          <svg
            className="w-full"
            height="60"
            viewBox="0 0 1200 60"
            fill="none"
            preserveAspectRatio="none"
          >
            {/* Animated horizontal line */}
            <line
              x1="0" y1="30" x2="1200" y2="30"
              stroke="url(#techLineGrad)"
              strokeWidth="1"
              strokeDasharray="6 4"
              opacity="0.3"
            />
            {/* Nodes */}
            {[0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1].map((pct) => (
              <circle
                key={pct}
                cx={pct * 1200}
                cy={30}
                r={4}
                fill="#1586FF"
                opacity={visible ? 0.6 : 0}
                style={{ transition: `opacity 0.5s ease ${pct * 1.5}s` }}
              />
            ))}
            <defs>
              <linearGradient id="techLineGrad" x1="0" y1="0" x2="1200" y2="0" gradientUnits="userSpaceOnUse">
                <stop stopColor="#1586FF" stopOpacity="0" />
                <stop offset="0.2" stopColor="#1586FF" />
                <stop offset="0.5" stopColor="#D3A6FF" />
                <stop offset="0.8" stopColor="#1586FF" />
                <stop offset="1" stopColor="#1586FF" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {[
              { val: '99.9%', label: 'Uptime SLA' },
              { val: '<200ms', label: 'API Response' },
              { val: 'SOC2', label: 'Security Standard' },
              { val: '24/7', label: 'Infrastructure Monitoring' },
            ].map((s) => (
              <div key={s.label} className="text-center bg-white/[0.04] border border-white/[0.08] rounded-xl py-4 px-3">
                <p className="font-NeueMontreal text-[22px] md:text-[26px] font-bold text-white leading-none mb-1">{s.val}</p>
                <p className="schibsted-grotesk text-white/40 text-[11px]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
